import React from "react";
import { Modal, ModalBody, ModalHeader } from "reactstrap";
import { translateService, signService } from "../../services/imports";
import signers from "../../services/signerManagementService";
import { LoadingButton } from "../../components/formcontrols/imports";

const t = (k, ...a) => translateService.t(`signers.credentials.${k}`, ...a);
const empty = { companyKey: "", hmacSecret: "", configId: "" };

// The workspace owner enters or replaces a signing tool's credentials at its
// verification engine (Identomat: company key, HMAC secret, configuration id).
// The gateway checks them with the engine first and never shows them again:
// password fields, never prefilled, cleared when the dialog closes, never
// logged. A refusal keeps the form open with the engine's reason.
//
// props: isOpen, workspaceId, signer, note (after the creation), onClose(),
// onSaved(signer)
export default class CredentialsDialog extends React.Component {
  state = { ...empty, sending: false, refusal: null };

  componentDidUpdate(prev) {
    if (prev.isOpen && !this.props.isOpen) this.setState({ ...empty, refusal: null });
    if (!prev.isOpen && this.props.isOpen) this.setState({ ...empty, refusal: null, sending: false });
  }

  componentWillUnmount() {
    this.closed = true;
  }

  save = () => {
    const { companyKey, hmacSecret, configId } = this.state;
    if (!companyKey.trim() || !hmacSecret.trim()) return this.setState({ refusal: t("required") });
    const body = { companyKey: companyKey.trim(), hmacSecret: hmacSecret.trim() };
    if (configId.trim()) body.configId = configId.trim();
    this.setState({ sending: true, refusal: null });
    signers.setCredentials(this.props.workspaceId, this.props.signer.uuid, body).subscribe(
      (res) => {
        if (this.closed) return;
        this.setState({ ...empty, sending: false });
        this.props.onSaved(res.data);
      },
      (e) => {
        if (this.closed) return;
        const code = signService.problemCode(e);
        const refusal =
          code === "VERIFICATION_ENGINE_UNAVAILABLE"
            ? t("unreachable")
            : signService.problemMessage(e, translateService.t("signers.actionError"));
        this.setState({ sending: false, refusal });
      }
    );
  };

  input = (name, label, type, placeholder) => (
    <label className="signer-dialog__field" htmlFor={`signer-${name}`}>
      <span>{label}</span>
      <input
        id={`signer-${name}`}
        type={type}
        autoComplete={type === "password" ? "new-password" : "off"}
        spellCheck="false"
        maxLength={1024}
        placeholder={placeholder}
        value={this.state[name]}
        onChange={(e) => this.setState({ [name]: e.target.value, refusal: null })}
      />
    </label>
  );

  render() {
    const signer = this.props.signer;
    return (
      <Modal isOpen={!!this.props.isOpen} fade={false} className={`signer-dialog ${translateService.key}`} toggle={this.props.onClose}>
        <ModalHeader>
          <span>{t("formTitle", (signer && signer.name) || "")}</span>
        </ModalHeader>
        <ModalBody>
          <div className="signer-dialog__scroll">
            {this.props.note && <p className="signer-dialog__note">{t("afterCreate")}</p>}
            {this.input("companyKey", t("companyKey"), "password")}
            {this.input("hmacSecret", t("hmacSecret"), "password")}
            {this.input("configId", t("configId"), "text", t("configDefault"))}
            <p className="signer-dialog__hint">{t("never")}</p>
            {signer && signer.credentials && <p className="signer-dialog__hint">{t("replaceWarning")}</p>}
            {this.state.refusal && (
              <p className="signer-dialog__error" role="alert">
                {this.state.refusal}
              </p>
            )}
          </div>
          <div className="buttons signer-dialog__footer">
            <LoadingButton type="button" className="button background-color-two color-white" loading={this.state.sending} onClick={this.save}>
              {t("save")}
            </LoadingButton>
            <LoadingButton
              type="button"
              className="button background-color-one color-white"
              disabled={this.state.sending}
              onClick={this.props.onClose}
            >
              {translateService.t("signers.cancel")}
            </LoadingButton>
          </div>
        </ModalBody>
      </Modal>
    );
  }
}
